import React, { useState, useEffect } from "react";
import { X, Plus, Pencil, Trash2, Globe, GripHorizontal } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { SOCIAL_ICONS } from "data/staticData";

export default function SocialLinksModal({ open, onClose, links, onChange }) {
  const [screen, setScreen] = useState("list"); // list | add | edit
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");

  const [editId, setEditId] = useState(null);

  // Reset modal state when closed
  useEffect(() => {
    if (!open) {
      setScreen("list");
      setSelectedPlatform(null);
      setLinkUrl("");
      setLinkName("");
      setEditId(null);
    }
  }, [open]);

  // Handle icon selection
  const handleSelectPlatform = (platformId) => {
    setSelectedPlatform(platformId);
    // If user is on the list screen and clicks an icon, move to "add" mode automatically
    if (screen === "list") {
      setScreen("add");
      setLinkUrl("");
      setLinkName("");
    }
  };

  const startAdd = () => {
    setScreen("add");
    setSelectedPlatform(null);
    setLinkUrl("");
    setLinkName("");
  };

  const startEdit = (item) => {
    setScreen("edit");
    setEditId(item.id);
    setSelectedPlatform(item.platform);
    setLinkUrl(item.url);
    setLinkName(item.name);
  };

  const saveLink = () => {
    if (!selectedPlatform || !linkUrl.trim()) return;

    const platformData = SOCIAL_ICONS.find((p) => p.id === selectedPlatform);

    // Logic: If custom, use linkName. If known platform, use its default label.
    const autoName =
      selectedPlatform !== "custom" ? (platformData?.label || "Link") : linkName.trim();

    let updated = [...links];

    if (screen === "edit") {
      updated = updated.map((i) =>
        i.id === editId
          ? {
            ...i,
            platform: selectedPlatform,
            url: linkUrl,
            name: autoName,
          }
          : i
      );
    } else {
      updated.push({
        id: Date.now(),
        platform: selectedPlatform,
        url: linkUrl.trim(),
        name: autoName,
      });
    }

    onChange(updated);
    setScreen("list");
    setSelectedPlatform(null);
  };

  const deleteLink = (id) => {
    onChange(links.filter((l) => l.id !== id));
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300
         ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center duration-200
         ${open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"} mx-10 lg:mx-0`}
      >
        <div className="bg-[#FDFAF7] w-[500px] rounded-2xl shadow-xl p-6 relative">

          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              {screen === "list" ? "Social Links" : screen === "edit" ? "Edit Link" : "Add Link"}
            </h2>
            <button onClick={onClose}>
              <X className="text-gray-400 hover:text-black transition-colors" />
            </button>
          </div>

          <p className="text-sm text-gray-500 mb-5">
            Add links that showcase your work, recognition, personality and more!
          </p>

          {/* Platform Selector */}
          <div className="flex flex-wrap gap-2 lg:gap-3 mb-5">
            {SOCIAL_ICONS.map((s) => {
              const isSelected = selectedPlatform === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => handleSelectPlatform(s.id)}
                  className={`flex flex-col items-center gap-1 border rounded-xl p-2 lg:p-3 transition-all
                    ${isSelected
                        ? "border-black shadow-sm"
                        : "bg-white border-gray-100 hover:border-gray-300"
                    }`}
                >
                  {s?.icon ? (
                    <s.icon className={`w-5 h-5 ${isSelected ? "" : "text-gray-600"}`} />
                  ) : (
                    <img src={s.src} alt={s.label} className="w-5 h-5" />
                  )}
                </button>
              );
            })}
          </div>

          {/* ADD / EDIT Screen */}
          {(screen === "add" || screen === "edit") && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600 ml-1">Link URL</label>
                <Input
                  placeholder="https://your-link.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="h-11"
                />
              </div>

              {/* ONLY show Name input if platform is CUSTOM */}
              {selectedPlatform === "custom" && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600 ml-1">Display Name</label>
                  <Input
                    placeholder="e.g. Portfolio, Blog, etc."
                    value={linkName}
                    onChange={(e) => setLinkName(e.target.value)}
                    className="h-11"
                  />
                </div>
              )}

              <div className="flex justify-end mt-6 gap-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setScreen("list");
                    setSelectedPlatform(null);
                  }}
                  className="rounded-full px-6"
                >
                  Cancel
                </Button>

                <Button
                  onClick={saveLink}
                  disabled={!linkUrl.trim() || !selectedPlatform}
                  className="rounded-full px-6"
                >
                  {screen === "edit" ? "Update Link" : "Add Link"}
                </Button>
              </div>
            </div>
          )}

          {/* LIST Screen */}
          {screen === "list" && (
            <>
              <div className="flex justify-between items-center mb-3">
                 <p className="text-sm text-gray-500 font-medium">{links.length}/7 links added</p>
              </div>

              <div className="space-y-3 max-h-[260px] overflow-auto pr-2 custom-scrollbar">
                {links.length === 0 ? (
                    <div className="py-10 text-center border-2 border-dashed border-gray-200 rounded-xl text-gray-400">
                        No links added yet. Click an icon above to start.
                    </div>
                ) : (
                    links.map((item) => {
                    const platform = SOCIAL_ICONS.find((i) => i.id === item.platform);
                    return (
                        <div
                        key={item.id}
                        className="flex items-center justify-between bg-white p-3 rounded-xl border border-gray-50 shadow-sm"
                        >
                        <div className="flex items-center gap-3">
                            <GripHorizontal size={16} className="text-gray-300 cursor-grab" />

                            {platform?.src ? (
                            <img src={platform.src} className="w-5 h-5" alt={platform.label} />
                            ) : platform?.icon ? (
                            <platform.icon className="w-5 h-5 text-gray-600" />
                            ) : (
                            <Globe className="w-5 h-5 text-gray-600" />
                            )}

                            <span className="text-sm font-medium text-gray-700">{item.name}</span>
                        </div>

                        <div className="flex gap-2">
                            <button
                            onClick={() => startEdit(item)}
                            className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors"
                            title="Edit"
                            >
                            <Pencil size={15} />
                            </button>
                            <button
                            onClick={() => deleteLink(item.id)}
                            className="p-2 rounded-lg bg-gray-50 hover:bg-red-50 hover:text-red-600 text-gray-600 transition-colors"
                            title="Delete"
                            >
                            <Trash2 size={15} />
                            </button>
                        </div>
                        </div>
                    );
                    })
                )}
              </div>

              {/* Add Link Button */}
              {links.length < 7 && (
                <button
                    className="flex items-center gap-2 mt-5 text-gray-500 transition-colors group"
                    onClick={startAdd}
                >
                    <div className="w-8 h-8 rounded-full border border-dashed border-gray-300 flex items-center justify-center">
                    <Plus size={16} />
                    </div>
                    <span className="text-sm font-medium">Add another link</span>
                </button>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-3 mt-8 border-t pt-4">
                <Button variant="outline" onClick={onClose} className="rounded-full px-8">
                  Close
                </Button>
                <Button
                  onClick={onClose}
                  className="rounded-full px-8 bg-black text-white hover:bg-gray-800"
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
